import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../services/authService';

import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Divider
} from '@mui/material';

import TrainIcon from '@mui/icons-material/Train';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from "@mui/material/Badge";

import useNotifications from "../../app/hooks/useNotifications";
import { useMinhaLinhaOperador } from "../../app/hooks/useMinhaLinhaOperador";
import ConfirmModal from '../ConfirmModal/ConfirmModal';

import './Sidebar.css';

const SIDEBAR_WIDTH = 72;

const NAV_ITEMS = [
  { label: 'Trens', icon: <TrainIcon fontSize="medium" />, path: '/dashboard' },
  { label: 'Cadastros', icon: <EditNoteIcon fontSize="medium" />, path: '/cadastroPage' },
  { label: 'Histórico', icon: <HistoryIcon fontSize="medium" />, path: '/telemetria' },
  { label: 'Alocações', icon: <SyncAltIcon fontSize="medium" />, path: '/alocacoes' },
  { label: 'Logs', icon: <ListAltIcon fontSize="medium" />, path: '/eventos' },
  { label: 'Online', icon: <PersonIcon fontSize="medium" />, path: '/usuarios-logados' },
  { label: 'Notificações', icon: <NotificationsIcon fontSize="medium" />, path: '/notificacoes' },
];

export default function Sidebar() {
  const notifications = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuth();
  const { idLinha } = useMinhaLinhaOperador(userInfo);

  const [confirmLogout, setConfirmLogout] = useState(false);

  const isAdmin =
    userInfo?.roleId === 1 ||
    String(userInfo?.roleNome).toLowerCase() === "administrador";
  const isVisualizador = !idLinha;

  const hasUnread = useMemo(() => {
    return notifications.some((n) => {
      if (n.readAt) return false;

      if (isAdmin) return true;

      if (isVisualizador) return false;

      return Number(n.linhaId) === Number(idLinha);
    });
  }, [notifications, isAdmin, isVisualizador, idLinha]);

  const handleLogout = () => setConfirmLogout(true);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');
  return (
    <>
      <ConfirmModal
        show={confirmLogout}
        type="confirm"
        message="Deseja realmente sair do sistema?"
        onClose={() => setConfirmLogout(false)}
        onConfirm={logout}
      />

      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { border: 'none' },
        }}
        slotProps={{ paper: { className: 'sidebar-paper' } }}
      >
        <div className="sidebar-menu-icon">
          <MenuIcon />
        </div>

        <Divider className="sidebar-divider" />

        <List className="sidebar-nav-list">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);

            return (
              <Tooltip title={item.label} placement="right" key={item.label}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    className={`sidebar-nav-btn${active ? ' active' : ''}`}
                  >
                    <ListItemIcon className="sidebar-nav-icon">
                      {item.label === "Notificações" ? (
                        <Badge
                          color="error"
                          variant="dot"
                          overlap="circular"
                          invisible={!hasUnread}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>

                    <span className={`sidebar-nav-label${active ? ' active' : ''}`}>
                      {item.label}
                    </span>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
        </List>

        <Divider className="sidebar-divider" />

        <List className="sidebar-logout-list">
          <Tooltip title="Logout" placement="right">
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                className="sidebar-nav-btn sidebar-logout-btn"
              >
                <ListItemIcon className="sidebar-nav-icon">
                  <LogoutIcon fontSize="medium" />
                </ListItemIcon>
                <span className="sidebar-nav-label">Logout</span>
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </List>
      </Drawer>
    </>
  );
}